import {
  Pagination as HeroPagination,
  PaginationProps as HeroPaginationProps,
} from "@heroui/react";
import { useControlledState } from "@react-stately/utils";
import clsx from "clsx";
import { useA11yMessage } from "containers/A11yStatusProvider/A11yStatusContext";
import { useEffect, useMemo } from "react";
import { useTranslations } from "translations";
import { Select, SelectProps } from "../select/select";

/**
 * Pagination component that provides page navigation and rows per page selection.
 * Supports both controlled and uncontrolled modes.
 */
export type PaginationProps = Omit<
  HeroPaginationProps,
  "children" | "showShadow" | "isDisabled"
> & {
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  page?: number;
  rowsPerPage?: number;
  rowsPerPageOptions?: readonly number[];
  selectVariant?: SelectProps<{ id: string; label: string }>["variant"];
  showRowsPerPage?: boolean;
  total?: number;
};

const DEFAULT_ROWS_PER_PAGE = 10;
const DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100] as const;

function usePagination({
  onPageChange,
  onRowsPerPageChange,
  page: controlledPage,
  rowsPerPage: controlledRowsPerPage,
  total,
}: {
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  page?: number;
  rowsPerPage?: number;
  rowsPerPageOptions?: number[];
  total?: number;
}) {
  const [page, setPage] = useControlledState(controlledPage, 1, onPageChange);

  const [rowsPerPage, setRowsPerPage] = useControlledState(
    controlledRowsPerPage,
    DEFAULT_ROWS_PER_PAGE,
    onRowsPerPageChange,
  );

  const totalPages = useMemo(() => {
    if (total === undefined || total <= 0) return 0;
    return Math.ceil(total / rowsPerPage);
  }, [total, rowsPerPage]);

  // Ensure page is within valid range
  const validPage = useMemo(() => {
    if (totalPages === 0) return 1;
    return Math.max(1, Math.min(page, totalPages));
  }, [page, totalPages]);

  return {
    page: validPage,
    rowsPerPage,
    setPage,
    setRowsPerPage,
    totalPages,
  };
}

export function Pagination({
  onPageChange,
  onRowsPerPageChange,
  page: controlledPage,
  rowsPerPage: controlledRowsPerPage,
  rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
  showRowsPerPage = true,
  selectVariant = "horizontal",
  total,
  ...props
}: PaginationProps) {
  const translations = useTranslations();
  const { page, rowsPerPage, setPage, setRowsPerPage, totalPages } =
    usePagination({
      onPageChange,
      onRowsPerPageChange,
      page: controlledPage,
      rowsPerPage: controlledRowsPerPage,
      total,
    });

  const { updateA11yMessage } = useA11yMessage();

  useEffect(() => {
    if (total && totalPages > 0) {
      updateA11yMessage(
        translations.accessibility.paginationCount({
          page: `${page}`,
          totalPages: `${totalPages}`,
        }),
      );
    }
  }, [page, totalPages, total, updateA11yMessage]);

  const selectItems = useMemo(
    () =>
      rowsPerPageOptions.map((value) => ({
        id: String(value),
        label: String(value),
      })),
    [rowsPerPageOptions],
  );

  const renderRowsPerPageSelector = () => {
    if (!showRowsPerPage) return null;

    return (
      <div className="flex items-center gap-2">
        <Select
          aria-label="Select rows per page"
          className="w-20"
          items={selectItems}
          label="Rows per page"
          onSelectionChange={(keys) => {
            const selectedKey = Array.from(keys)[0];
            if (selectedKey) {
              setRowsPerPage(Number(selectedKey));
            }
          }}
          renderItem={(item) => item.label}
          selectedKeys={[String(rowsPerPage)]}
          size="sm"
          variant={selectVariant}
        />
      </div>
    );
  };

  if (total === undefined || total <= 0 || totalPages === 0) return null;

  return (
    <div className="flex items-center justify-between gap-4">
      {renderRowsPerPageSelector()}
      <HeroPagination
        {...props}
        page={page}
        total={totalPages}
        onChange={setPage}
        classNames={{
          wrapper: clsx("shadow-none", {
            "bg-gray-400": props.isCompact,
            "": !props.isCompact,
          }),
          cursor: clsx("shadow-none"),
          item: clsx("shadow-none text-gray-900", {
            "bg-gray-100": props.isCompact,
            "border-1 border-gray-500 bg-white": !props.isCompact,
          }),
          prev: clsx(
            "shadow-none text-gray-900",
            "data-[disabled=true]:bg-gray-200 data-[disabled=true]:text-gray-900",
            {
              "bg-gray-100": props.isCompact,
              "border-1 border-gray-500 bg-white": !props.isCompact,
            },
          ),
          next: clsx(
            "shadow-none text-gray-900",
            "data-[disabled=true]:bg-gray-200 data-[disabled=true]:text-gray-900",
            {
              "bg-gray-100": props.isCompact,
              "border-1 border-gray-500 bg-white": !props.isCompact,
            },
          ),
          ellipsis: clsx("text-gray-900"),
          forwardIcon: clsx("text-gray-500"),
          chevronNext: clsx("text-gray-900"),
        }}
      />
    </div>
  );
}

Pagination.displayName = "RecareUI.Pagination";
